import React, { useEffect, useMemo, useRef, useState } from 'react';
import { EntityPicker } from '../v2/EntityPicker';
import { readIntentVersions } from '../../services/protectBuild';
import { NOTE } from './typeScale';

/**
 * Choosing what an agent is allowed to decide.
 *
 * <h3>Why a template and then a version</h3>
 *
 * A template is a name; a version is the thing that decides. The platform's own
 * bind endpoint takes a template VERSION id, and refuses anything that is not
 * APPROVED or ACTIVE — so asking for a template alone would be asking half the
 * question and guessing the other half. The console asks both, and so does this.
 *
 * <h3>Why the version's own properties are on screen</h3>
 *
 * `securityCritical` gives every bound agent a hard banner and auto-blocks it at
 * the Act phase. `hasBrokenRefs` means the version points at skills that are
 * gone, so the matchers it carries authorize nothing. Binding either one looked
 * exactly like binding a clean one, which meant the decision was being made
 * without the part of it that matters.
 *
 * <h3>Why the console's picker rather than a third one</h3>
 *
 * The search, paging, keyboard and click-outside behaviour already exists in
 * components/v2/EntityPicker, where every user-visible string is a prop. A copy
 * styled for this surface would be the same widget twice, drifting from the
 * first keyboard fix that lands in one of them. Instead the surrounding
 * .p7-v2-bridge re-binds the --v2-* tokens it draws from to their --p7-*
 * equivalents, so it arrives in the governance palette without being forked.
 */
export default function IntentPicker({ intents = [], onPick, disabled = false }) {
  const [templateId, setTemplateId] = useState(null);
  const [versions, setVersions] = useState([]);
  const [versionId, setVersionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [problem, setProblem] = useState(null);

  /* Held in a ref so a caller that rebuilds its handler every render does not
     re-fire the read, and so the effect below needs no dependency on it. */
  const report = useRef(onPick);
  useEffect(() => { report.current = onPick; }, [onPick]);

  const template = useMemo(
    () => intents.find((i) => i.templateId === templateId) || null,
    [intents, templateId],
  );

  useEffect(() => {
    if (!templateId) {
      setVersions([]); setVersionId(null); setProblem(null); setLoading(false);
      return undefined;
    }
    let live = true;
    setLoading(true); setProblem(null); setVersions([]); setVersionId(null);
    readIntentVersions(templateId).then((out) => {
      if (!live) return;
      setVersions(out.versions || []);
      /* Newest bindable version first out of the service, so opening on the
         first one opens on the current policy rather than an old one. */
      setVersionId(out.versions?.[0]?.id || null);
      setProblem(out.error || null);
      setLoading(false);
    });
    return () => { live = false; };
  }, [templateId]);

  const chosen = useMemo(
    () => versions.find((v) => v.id === versionId) || null,
    [versions, versionId],
  );

  /* The answer this control exists to produce. Reported as the version,
     because the version is what gets bound. */
  useEffect(() => {
    if (!template || !chosen) { report.current?.(null); return; }
    report.current?.({
      templateId: template.templateId,
      versionId: chosen.id,
      label: `${template.label} · ${chosen.label}`,
      version: chosen,
    });
  }, [template, chosen]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--p7-space-3)' }}>
      <div className="p7-v2-bridge">
        <FieldName>What it is allowed to decide</FieldName>
        <EntityPicker
          items={intents}
          value={templateId}
          onChange={setTemplateId}
          disabled={disabled}
          ariaLabel="Intent template"
          getId={(x) => x.templateId}
          getLabel={(x) => x.label}
          getSublabel={(x) => x.description || null}
          getBadge={(x) => x.riskTier || null}
          getSearchText={(x) => [x.label, x.description, x.riskTier]
            .filter(Boolean).join(' ')}
          placeholder="Choose an approved intent"
          searchPlaceholder="Search approved intents"
          emptyMessage={(q, total) => (q
            ? `Nothing approved matches “${q}”. Clear the search to see all ${total}.`
            : 'No approved intent exists in this tenant yet.')}
        />
      </div>

      {template?.description ? (
        <p style={NOTE}>{template.description}</p>
      ) : null}

      {loading ? <p style={NOTE}>Reading its approved versions…</p> : null}

      {problem ? (
        <p className="p7-note p7-note--deny" style={{ margin: 0 }}>{problem}</p>
      ) : null}

      {template && !loading && !problem && versions.length === 0 ? (
        <p style={NOTE}>
          This intent has no approved version to bind to. Approval is what points a template
          at a version, so somebody with policy or compliance admin has to approve one first.
        </p>
      ) : null}

      {versions.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <FieldName as="label" htmlFor="intent-version">Version</FieldName>
          <select
            id="intent-version"
            value={versionId || ''}
            disabled={disabled}
            onChange={(e) => setVersionId(e.target.value)}
            style={{ padding: '9px 11px', border: '1px solid var(--p7-line-strong)',
                     borderRadius: 'var(--p7-radius-control)', font: 'var(--p7-text-body)',
                     fontFamily: 'var(--p7-font-sans)', background: 'var(--p7-surface)',
                     color: 'var(--p7-ink)' }}
          >
            {versions.map((v) => (
              <option key={v.id} value={v.id}>
                {v.state ? `${v.label} – ${v.state}` : v.label}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {chosen ? <VersionFacts v={chosen} /> : null}
    </div>
  );
}

/**
 * What the chosen version actually says.
 *
 * The two warnings come first because they are the only things here that change
 * whether binding it is a good idea at all.
 */
function VersionFacts({ v }) {
  const skills = v.allowedSkillMatchers || [];
  /* "*" is a glob, and the resolver runs it over the agent's whole skill
     universe at bind time. So it takes every skill the agent has at that
     moment and nothing added afterwards — a snapshot, which is the opposite
     of what a bare asterisk reads as. */
  const wideOpen = skills.some((m) => m.mode === 'skill-glob' && m.value === '*');
  const connectors = v.allowedConnectors || [];
  return (
    <div style={{ border: '1px solid var(--p7-line)',
                  borderRadius: 'var(--p7-radius-control)',
                  padding: '11px 13px', background: 'var(--p7-surface-sunken)',
                  display: 'flex', flexDirection: 'column', gap: '7px' }}>
      {v.securityCritical ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <span className="p7-status p7-status--deny" style={{ alignSelf: 'flex-start' }}>
            Security critical
          </span>
          <p style={NOTE}>
            {v.securityCriticalReason
              || 'Marked security critical, so an agent bound to it is held at the Act phase '
                 + 'until somebody with the authority to release it does so.'}
          </p>
        </div>
      ) : null}

      {v.hasBrokenRefs ? (
        <p className="p7-note p7-note--deny" style={{ margin: 0 }}>
          This version points at skills that no longer exist. What it still authorizes is
          whatever survived, so bind it only if you know which part that is.
        </p>
      ) : null}

      <Fact label="Autonomy">{v.maxAutonomy || 'Not set'}</Fact>
      <Fact label="Approval">
        {v.requiresApproval
          ? 'A human has to approve each decision'
          : 'No approval step on each decision'}
      </Fact>
      <Fact label="Skills it allows">
        {skills.length
          ? skills.map((m) => m.label).join(', ')
          : 'None — it authorizes no tool call'}
      </Fact>
      {skills.length ? (
        <p style={{ ...NOTE, marginTop: '-2px' }}>
          {wideOpen
            ? 'This allows every skill this agent has at the moment you bind it, and only '
              + 'those — what matches is frozen on the binding, so a skill added afterwards '
              + 'is not covered until it is bound again.'
            : 'These are matched against this agent’s own skills when you bind, and what '
              + 'matches is frozen on the binding. A skill added afterwards is not covered '
              + 'until it is bound again.'}
        </p>
      ) : null}
      <Fact label="Connectors">
        {connectors.length ? connectors.join(', ') : 'None'}
      </Fact>
    </div>
  );
}

function Fact({ label, children }) {
  return (
    <div style={{ display: 'flex', gap: '9px', alignItems: 'baseline', flexWrap: 'wrap' }}>
      <span style={{ font: 'var(--p7-text-caption)', fontFamily: 'var(--p7-font-sans)',
                     color: 'var(--p7-ink-subtle)', minWidth: '132px' }}>
        {label}
      </span>
      <span style={{ font: 'var(--p7-text-ui)', fontFamily: 'var(--p7-font-sans)',
                     color: 'var(--p7-ink)', fontWeight: 500, minWidth: 0,
                     overflowWrap: 'anywhere' }}>
        {children}
      </span>
    </div>
  );
}

/**
 * The caption above a control — a <label> only when it has a control to
 * point at. A label element bound to nothing is announced as the label for
 * whatever happens to follow it, which here would be a lie.
 */
function FieldName({ as, htmlFor, children }) {
  const isLabel = as === 'label';
  const Tag = isLabel ? 'label' : 'span';
  return (
    <Tag
      htmlFor={isLabel ? htmlFor : undefined}
      style={{ display: 'block', marginBottom: isLabel ? 0 : '5px',
               font: 'var(--p7-text-caption)', fontFamily: 'var(--p7-font-sans)',
               color: 'var(--p7-ink-muted)', fontWeight: 500 }}
    >
      {children}
    </Tag>
  );
}

